import { SiteLayout } from '@/components/site/SiteLayout'
import { PolicyShell, PolicySection } from '@/components/site/PolicyShell'

const UPDATED = '2026-08-26'

export function PrivacyPage() {
  return (
    <SiteLayout>
      <PolicyShell title="Privacy" updated={UPDATED}>
        <PolicySection heading="What this page is">
          <p>
            GeoBorder is a free, open service for resolving geographic
            coordinates to their jurisdictional context. This page describes,
            in plain language, what information we collect when you use the
            site or developer API, how we use that information, and what we
            don't do with it.
          </p>
        </PolicySection>

        <PolicySection heading="What we collect">
          <ul>
            <li>
              <strong>Account info</strong> if you create one — your email
              address and a one-way hashed password.
            </li>
            <li>
              <strong>API key metadata</strong> for keys you mint — the key's
              SHA-256 hash, an opaque prefix, optional name and expiration.
              We never store the plaintext key; it's shown to you exactly once.
            </li>
            <li>
              <strong>Request logs</strong> for every API call — the endpoint,
              the input coordinate, the response status, response time, your
              IP address, user agent, referer, and a request ID. Used for
              rate limiting, abuse triage, debugging, and aggregate usage
              metrics.
            </li>
            <li>
              <strong>Browser storage</strong> on the homepage — a local
              theme preference (light / dark) and standard authentication
              session tokens managed by our hosting provider.
            </li>
          </ul>
        </PolicySection>

        <PolicySection heading="What we don't do">
          <ul>
            <li>We don't sell, rent, or share personal information.</li>
            <li>We don't run advertising and don't embed third-party trackers.</li>
            <li>
              We don't combine your usage with profiles from other services or
              data brokers.
            </li>
            <li>We don't store your plaintext password or your plaintext API key.</li>
          </ul>
        </PolicySection>

        <PolicySection heading="Third parties we use">
          <p>
            GeoBorder runs on infrastructure operated by third parties. Their
            handling of data is governed by their own policies:
          </p>
          <ul>
            <li>
              <strong>Hosting + database:</strong> Supabase (PostgreSQL,
              authentication, edge functions).
            </li>
            <li>
              <strong>Static hosting + CDN:</strong> Netlify.
            </li>
            <li>
              <strong>Map tiles + attribution:</strong> OpenFreeMap basemaps,
              Esri World Imagery for satellite, OpenStreetMap data. A
              deployment may instead be configured to serve street tiles from
              CARTO or satellite imagery from the U.S. Geological Survey; the
              credit shown on the map always names the provider actually
              serving it.
            </li>
          </ul>
          <p>
            Boundary data is sourced from public, openly licensed datasets
            (primarily U.S. Census TIGER/Line and Natural Earth) and is not
            personal information.
          </p>
        </PolicySection>

        <PolicySection heading="Data retention">
          <p>
            Request logs are retained for operational purposes (rate limiting,
            abuse response, troubleshooting) and may be aggregated for
            anonymous usage statistics. We may delete or anonymize older log
            entries at our discretion.
          </p>
        </PolicySection>

        <PolicySection heading="Your choices">
          <p>
            You can stop using the service at any time. Revoking an API key
            from your account dashboard prevents future use of that key.
            Signing out clears your session in the browser.
          </p>
        </PolicySection>

        <PolicySection heading="Changes to this policy">
          <p>
            We may update this page as the service evolves. The "last updated"
            date at the top reflects the most recent revision. Continued use
            of GeoBorder after changes constitutes acceptance of the revised
            policy.
          </p>
        </PolicySection>
      </PolicyShell>
    </SiteLayout>
  )
}
